import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";
import { formatApiErrorDetail } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setDemoCode(data.demo_code);
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
      toast.success("Password reset successfully. Sign in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Reset password" subtitle={step === 1 ? "Enter your account email to receive a verification code." : "Enter the verification code and choose a new password."}>
      {step === 1 ? (
        <form onSubmit={requestCode} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Email</Label>
            <Input data-testid="forgot-email-input" type="email" required className="bg-white border-slate-300" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="w-full bg-[#0050A0] text-white py-3.5 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200 disabled:opacity-60">
            {busy ? "Sending…" : "Send Verification Code"}
          </button>
          <p className="text-sm text-slate-500 text-center">
            Remembered it?{" "}
            <Link to="/login" data-testid="forgot-goto-login-link" className="text-[#0050A0] font-medium hover:text-[#009EDB] transition-colors duration-200">Sign in</Link>
          </p>
        </form>
      ) : (
        <form onSubmit={resetPassword} className="space-y-5">
          <div className="bg-[#E6F5FC] border border-[#009EDB]/40 p-4 flex items-start gap-3" data-testid="demo-code-banner">
            <MailCheck className="w-5 h-5 text-[#0050A0] mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <p className="text-sm text-slate-700">
                Demo mode — your verification code is{" "}
                <span className="font-mono font-bold text-[#0050A0] text-lg tracking-widest" data-testid="demo-code-value">{demoCode}</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">In production this code would be emailed to {email}. Valid for 10 minutes.</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">6-Digit Verification Code</Label>
            <Input data-testid="reset-code-input" required maxLength={6} inputMode="numeric" className="bg-white border-slate-300 font-mono text-lg tracking-[0.5em]" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">New Password</Label>
            <Input data-testid="reset-password-input" type="password" required minLength={6} className="bg-white border-slate-300" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-submit-btn" className="w-full bg-[#0050A0] text-white py-3.5 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200 disabled:opacity-60">
            {busy ? "Resetting…" : "Reset Password"}
          </button>
          <button type="button" data-testid="resend-code-btn" onClick={() => { setStep(1); setCode(""); setError(""); }} className="w-full text-sm text-slate-500 hover:text-[#0050A0] transition-colors duration-200">
            Use a different email / resend code
          </button>
        </form>
      )}
    </AuthShell>
  );
}
